"use client";

import { Input } from "@/components/ui/input";
import { Card } from "@heroui/card";
import { useFormContext } from "react-hook-form";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Separator } from "@/components/ui/separator";
import { FlaskConical, Milk, TrendingUp } from "lucide-react";

export function MilkProperties() {
  const { control } = useFormContext();

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <div className="flex items-center justify-center gap-2">
          <Milk className="h-6 w-6 text-pink" />
          <h2 className="text-2xl font-bold text-[#cfc130ff]">
            Milk Composition Analysis
          </h2>
        </div>
        <p className="text-muted-foreground ">
          Record milk production data and protein composition analysis
        </p>
      </div>

      {/* Production Metrics Section */}
      <div className="space-y-6">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-green-600" />
          <h3 className="text-lg font-semibold">Production Metrics</h3>
        </div>

        <div className="bg-slate-50 dark:bg-slate-800 p-4 rounded-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Milk Production */}
            <div className="space-y-4">
              <FormField
                control={control}
                name="milkKg"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={"text-[#008c8fff]"}>
                      Milk (Kg)
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="any"
                        placeholder="Milk production in Kg"
                        {...field}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value ? Number(e.target.value) : undefined,
                          )
                        }
                      />
                    </FormControl>
                    <FormDescription>Milk production in (Kg)</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Age 1 P */}
            <div className="space-y-4">
              <FormField
                control={control}
                name="age1P"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={"text-[#008c8fff]"}>
                      Age 1 P
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="any"
                        placeholder="ability to produce milk at 1st parity"
                        {...field}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value ? Number(e.target.value) : undefined,
                          )
                        }
                      />
                    </FormControl>
                    <FormDescription></FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* EBI */}
            <div className="space-y-4">
              <FormField
                control={control}
                name="ebi"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={"text-[#008c8fff]"}>EBI</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="any"
                        placeholder="ebi value"
                        {...field}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value ? Number(e.target.value) : undefined,
                          )
                        }
                      />
                    </FormControl>
                    <FormDescription>
                      Measurement accuracy (0-100)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Accuracy */}
            <div className="space-y-4">
              <FormField
                control={control}
                name="milkAcc"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={"text-[#008c8fff]"}>
                      Milk Accuracy
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="any"
                        placeholder="Milk production accuracy "
                        {...field}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value ? Number(e.target.value) : undefined,
                          )
                        }
                      />
                    </FormControl>
                    <FormDescription></FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Age 1 P */}
            <div className="space-y-4">
              <FormField
                control={control}
                name="age1pAcc"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={"text-[#008c8fff]"}>
                      Age 1 P Accuracy
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="any"
                        placeholder="Age 1 P accuracy"
                        {...field}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value ? Number(e.target.value) : undefined,
                          )
                        }
                      />
                    </FormControl>
                    <FormDescription>
                      Measurement accuracy (0-100)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* EBI */}
            <div className="space-y-4">
              <FormField
                control={control}
                name="ebiAcc"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={"text-[#008c8fff]"}>
                      EBI Accuracy
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="any"
                        placeholder="EBI accuracy"
                        {...field}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value ? Number(e.target.value) : undefined,
                          )
                        }
                      />
                    </FormControl>
                    <FormDescription>
                      Measurement accuracy (0-100)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        </div>
      </div>

      <Separator />

      {/* Protein Composition Section */}
      <div className="space-y-6">
        <div className="flex items-center gap-2">
          <FlaskConical className="h-5 w-5 text-purple-600" />
          <h3 className="text-lg font-semibold">
            Protein Composition Analysis
          </h3>
        </div>

        <div className="bg-slate-50 dark:bg-slate-800 p-4 rounded-lg">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            <FormField
              control={control}
              name="betaLac"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className={"text-[#008c8fff]"}>
                    Beta-Lactoglobulin (β-Lac)
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      placeholder="Enter β-Lac value"
                      {...field}
                      onChange={(e) =>
                        field.onChange(
                          e.target.value ? String(e.target.value) : undefined,
                        )
                      }
                    />
                  </FormControl>
                  <FormDescription>
                    Beta-lactoglobulin concentration
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="kappaC"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className={"text-[#008c8fff]"}>
                    Kappa-Casein (κ-C)
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      placeholder="Enter κ-C value"
                      {...field}
                      onChange={(e) =>
                        field.onChange(
                          e.target.value ? String(e.target.value) : undefined,
                        )
                      }
                    />
                  </FormControl>
                  <FormDescription>Kappa-casein concentration</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="betaC"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className={"text-[#008c8fff]"}>
                    Beta-Casein (β-C)
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      placeholder="Enter β-C value"
                      {...field}
                      onChange={(e) =>
                        field.onChange(
                          e.target.value ? String(e.target.value) : undefined,
                        )
                      }
                    />
                  </FormControl>
                  <FormDescription>Beta-casein concentration</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
      </div>

      <Card className="p-4 bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
        <div className="flex items-start gap-3">
          <FlaskConical className="h-5 w-5 text-[#008c8fff] mt-0.5 flex-shrink-0" />
          <div className="space-y-2">
            <h4 className="font-medium text-[#015354ff]">
              Milk Composition Analysis Information
            </h4>
            <p className="text-sm text-[#015354ff]">
              This section captures critical milk production and composition
              data used for genetic evaluation and breeding decisions.
              Production metrics help assess economic value, while protein
              composition analysis provides insights into milk quality and
              processing characteristics. Accuracy values indicate the
              reliability of each measurement for genetic evaluations.
            </p>
            <div className="text-xs text-[#015354ff] space-y-1">
              <p>
                <strong>Accuracy Range:</strong> 0.0 (low confidence) to 1.0
                (high confidence)
              </p>
              <p>
                <strong>Protein Analysis:</strong> Values typically measured in
                mg/mL or percentage
              </p>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
